import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../utils/analyticsEvents';
import { buildAppUrl } from '../utils/appLinks';

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const desktopAppLink = buildAppUrl({ medium: 'header', location: 'header_desktop' });
  const mobileAppLink = buildAppUrl({ medium: 'mobile_menu', location: 'header_mobile_menu' });

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  function handleLinkClick() {
    setMenuOpen(false);
  }

  return (
    <header className="sticky top-0 z-40 bg-[#F2F0E9]/95 backdrop-blur border-b border-[#D4CFC6]" style={{ borderBottomWidth: '1px' }}>
      {/* Clay accent strip at very top */}
      <div className="h-[3px] w-full bg-[#CC5833] absolute top-0 left-0" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-display text-lg tracking-[0.22em] uppercase text-[#2E4036] hover:text-[#CC5833] transition-colors">
          SistaKyerewaa
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.22em] text-[#8C7B6B]">
          <Link to="/blog" className="hover:text-[#2E4036] transition-colors">Blog</Link>
          <Link to="/store" className="hover:text-[#2E4036] transition-colors">Store</Link>
          <Link to="/about" className="hover:text-[#2E4036] transition-colors">About</Link>
          <Link to="/checklist" className="hover:text-[#2E4036] transition-colors">Checklist</Link>
          <Link to="/book" className="hover:text-[#2E4036] transition-colors">Book</Link>
          <a
            href={desktopAppLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, { location: 'header_desktop', outbound: true })}
            className="px-4 py-1.5 bg-[#2E4036] text-[#F2F0E9] hover:bg-[#1a2a20] transition-colors"
          >
            Start App
          </a>
        </nav>

        <button
          className="md:hidden text-[#2E4036] focus:outline-none focus:ring-2 focus:ring-[#8C7B6B] p-2"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg
            className="w-7 h-7"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div
          className="fixed top-0 left-0 w-screen h-screen z-[9999] md:hidden bg-[#F2F0E9]"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
            <span className="text-xs uppercase tracking-[0.25em] text-[#CC5833]">Menu</span>
            <button
              type="button"
              onClick={handleLinkClick}
              className="text-xs uppercase tracking-[0.2em] text-[#8C7B6B] hover:text-[#2E4036] transition-colors"
            >
              Close
            </button>
          </div>
          <nav
            ref={menuRef}
            className="px-6 py-8 flex flex-col gap-5 overflow-y-auto h-[calc(100vh-64px)]"
          >
            <Link to="/blog" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>Blog</Link>
            <Link to="/store" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>Store</Link>
            <Link to="/about" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>About</Link>
            <Link to="/checklist" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>Checklist</Link>
            <Link to="/book" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>Book a Session</Link>
            <Link to="/partners" className="text-[#2E4036] text-base uppercase tracking-[0.2em] border-l-2 border-transparent hover:border-[#CC5833] pl-3 transition-all" onClick={handleLinkClick}>Partners</Link>
            <a
              href={mobileAppLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors mt-2"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, { location: 'header_mobile_menu', outbound: true });
                handleLinkClick();
              }}
            >
              Start Learning App
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
